import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  CreditCardIcon,
  CalendarIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { AccountUsageChart, APICallsChart, UsageTrendsChart } from '../components/UsageChart';
import BillingActions from '../components/BillingActions';
import api from '../services/api';

// Only initialize Stripe if the publishable key is configured
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentMethodForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Update payment method via API
      const response = await api.post('/subscriptions/payment-method', {
        payment_method_id: paymentMethod.id,
      });

      onSuccess('Payment method updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update payment method');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <ErrorMessage message={error} />}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Payment Method
        </label>
        <div className="border border-gray-300 rounded-lg p-3 bg-white">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {processing ? <LoadingSpinner size="sm" /> : 'Update Payment Method'}
        </button>
      </div>
    </form>
  );
};

const Billing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [apiCallsData, setApiCallsData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  const [message, setMessage] = useState(location.state?.message ? {
    type: location.state.type || 'info',
    text: location.state.message
  } : null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchBillingData();
  }, [user, navigate]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscription and billing history in parallel
      const [subscriptionResponse, paymentsResponse] = await Promise.all([
        api.get('/subscriptions'),
        api.get('/subscriptions/payments')
      ]);

      setSubscription(subscriptionResponse.data);
      setPayments(paymentsResponse.data || []);
      
      // Fetch usage metrics after main data
      fetchUsageMetrics();
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load billing information'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageMetrics = async () => {
    try {
      setLoadingMetrics(true);
      
      const [apiCallsResponse, usageResponse] = await Promise.all([
        api.get('/analytics/api-calls?days=30').catch(() => ({ data: [] })),
        api.get('/analytics/usage-trends?days=30').catch(() => ({ data: [] }))
      ]);
      
      setApiCallsData(apiCallsResponse.data || []);
      setUsageData(usageResponse.data || []);
    } catch (error) {
      console.error('Error fetching usage metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        'Are you sure you want to cancel your subscription? You will continue to have access until your current billing period ends.'
      )
    ) {
      return;
    }

    try {
      setUpdating(true);

      const response = await api.post('/subscriptions/cancel', {
        immediate: false,
      });

      setMessage({
        type: 'success',
        text: 'Subscription cancelled successfully. Access continues until the end of your billing period.',
      });

      await fetchBillingData();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to cancel subscription',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePaymentMethod = (successMessage) => {
    setShowPaymentForm(false);
    setMessage({ type: 'success', text: successMessage });
    fetchBillingData();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getSubscriptionStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-800 bg-green-100 border-green-200';
      case 'trialing': return 'text-blue-800 bg-blue-100 border-blue-200';
      case 'past_due': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'canceled': return 'text-red-800 bg-red-100 border-red-200';
      case 'paused': return 'text-gray-800 bg-gray-100 border-gray-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircleIcon className="h-4 w-4" />;
      case 'trialing': return <ClockIcon className="h-4 w-4" />;
      case 'past_due': return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'canceled': return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'paused': return <ClockIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getDaysUntilBilling = (nextBillingDate) => {
    const now = new Date();
    const billing = new Date(nextBillingDate);
    const diffTime = billing.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getPaginatedPayments = () => {
    const indexOfLastPayment = currentPage * paymentsPerPage;
    const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
    return payments.slice(indexOfFirstPayment, indexOfLastPayment);
  };

  const totalPages = Math.ceil(payments.length / paymentsPerPage);

  const downloadInvoice = async (paymentId) => {
    try {
      const response = await api.get(`/subscriptions/payments/${paymentId}/invoice`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to download invoice'
      });
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'text-green-800 bg-green-100';
      case 'pending':
        return 'text-yellow-800 bg-yellow-100';
      case 'failed':
        return 'text-red-800 bg-red-100';
      default:
        return 'text-gray-800 bg-gray-100';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Billing Dashboard</h1>
            <p className="text-primary-100 mt-1">Manage your subscription, usage, and billing information</p>
          </div>
          <div className="hidden md:flex items-center space-x-4 text-right">
            <div>
              <div className="text-2xl font-bold">{subscription?.subscription ? formatCurrency(subscription.subscription.monthly_price) : '$0'}</div>
              <div className="text-primary-200 text-sm">Monthly</div>
            </div>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl border ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' : 
          message.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
          'bg-blue-50 text-blue-800 border-blue-200'
        } shadow-sm`}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {message.type === 'success' && <CheckCircleIcon className="h-5 w-5 text-green-400" />}
              {message.type === 'error' && <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />}
              {message.type === 'info' && <CheckCircleIcon className="h-5 w-5 text-blue-400" />}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{message.text}</p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
            {subscription?.subscription?.status && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium border inline-flex items-center space-x-1 ${getSubscriptionStatusColor(subscription.subscription.status)}`}>
                {getStatusIcon(subscription.subscription.status)}
                <span>{subscription.subscription.status.charAt(0).toUpperCase() + subscription.subscription.status.slice(1)}</span>
              </span>
            )}
          </div>

          {subscription?.subscription ? (
            <>
              {/* Plan Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-blue-600 font-medium">Plan Tier</div>
                      <div className="text-xl font-bold text-blue-900">
                        {subscription.subscription.tier.charAt(0).toUpperCase() + subscription.subscription.tier.slice(1)}
                      </div>
                    </div>
                    <ChartBarIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-green-600 font-medium">Monthly Cost</div>
                      <div className="text-xl font-bold text-green-900">
                        {formatCurrency(subscription.subscription.monthly_price)}
                      </div>
                    </div>
                    <CreditCardIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-purple-600 font-medium">Accounts Used</div>
                      <div className="text-xl font-bold text-purple-900">
                        {subscription.current_usage?.social_accounts || 0} / {subscription.subscription.account_limit}
                      </div>
                      {/* Usage Progress Bar */}
                      <div className="mt-2 w-full bg-purple-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${Math.min(100, (subscription.current_usage?.social_accounts || 0) / subscription.subscription.account_limit * 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-orange-600 font-medium">Next Billing</div>
                      <div className="text-lg font-bold text-orange-900">
                        {getDaysUntilBilling(subscription.next_billing_date)} days
                      </div>
                      <div className="text-xs text-orange-700 mt-1">
                        {formatDate(subscription.next_billing_date)}
                      </div>
                    </div>
                    <CalendarIcon className="h-8 w-8 text-orange-500" />
                  </div>
                </div>
              </div>

              {/* Plan Actions */}
              <div className="flex flex-wrap items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center space-x-4 mb-2 md:mb-0">
                  {subscription.payment_method && (
                    <div className="text-sm text-gray-600 flex items-center space-x-2">
                      <CreditCardIcon className="h-4 w-4" />
                      <span>•••• •••• •••• {subscription.payment_method.card?.last4}</span>
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-700">
                        {subscription.payment_method.card?.brand?.toUpperCase()}
                      </span>
                      {subscription.payment_method.card?.exp_month && subscription.payment_method.card?.exp_year && (
                        <span className="text-xs text-gray-500">
                          Exp: {subscription.payment_method.card.exp_month.toString().padStart(2, '0')}/{subscription.payment_method.card.exp_year.toString().slice(-2)}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Data retention:</span>
                    <span className="ml-1">{subscription.subscription.data_retention_days} days</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate('/pricing')}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm flex items-center space-x-1"
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                    <span>Upgrade Plan</span>
                  </button>
                  
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Update Payment
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <ChartBarIcon className="h-12 w-12" />
              </div>
              <div className="text-lg font-medium text-gray-900 mb-2">No Active Subscription</div>
              <div className="text-gray-500 mb-6">Choose a plan to get started with DZ Metrics</div>
              <button
                onClick={() => navigate('/pricing')}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                View Pricing Plans
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Usage Metrics Section */}
      {subscription?.subscription && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AccountUsageChart 
              currentUsage={subscription.current_usage?.social_accounts || 0}
              limit={subscription.subscription.account_limit}
              tier={subscription.subscription.tier}
            />
            
            {loadingMetrics ? (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-center">
                <LoadingSpinner />
                <span className="ml-2 text-gray-600">Loading metrics...</span>
              </div>
            ) : (
              <APICallsChart apiCallsData={apiCallsData} />
            )}
          </div>
          
          {!loadingMetrics && (
            <UsageTrendsChart usageData={usageData} />
          )}
        </>
      )}

      {/* Update Payment Method Form */}
      {showPaymentForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Update Payment Method</h2>
            <button
              onClick={() => setShowPaymentForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Close</span>
              ✕
            </button>
          </div>
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                onSuccess={handleUpdatePaymentMethod}
                onCancel={() => setShowPaymentForm(false)}
              />
            </Elements>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">Payment processing is currently unavailable.</p>
              <p className="text-sm text-gray-500 mt-2">Please contact support for assistance.</p>
            </div>
          )}
        </div>
      )}

      {/* Billing History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Billing History</h2>
            {payments.length > 0 && (
              <div className="text-sm text-gray-600">
                {payments.length} total payment{payments.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
          
          {payments.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getPaginatedPayments().map((payment) => (
                      <tr key={payment.payment_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(payment.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={payment.description}>
                            {payment.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(payment.amount, payment.currency?.toUpperCase())}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                            {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="text-xs">
                            {formatDate(payment.billing_period_start)}
                            <br />
                            to {formatDate(payment.billing_period_end)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => downloadInvoice(payment.payment_id)}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center space-x-1"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            <span>PDF</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {getPaginatedPayments().map((payment) => (
                  <div key={payment.payment_id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-900">
                        {formatDate(payment.created_at)}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(payment.status)}`}>
                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">{payment.description}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-lg font-semibold text-gray-900">
                        {formatCurrency(payment.amount, payment.currency?.toUpperCase())}
                      </div>
                      <button
                        onClick={() => downloadInvoice(payment.payment_id)}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center space-x-1"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        <span>Invoice</span>
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {formatDate(payment.billing_period_start)} - {formatDate(payment.billing_period_end)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * paymentsPerPage + 1} to{' '}
                    {Math.min(currentPage * paymentsPerPage, payments.length)} of{' '}
                    {payments.length} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 border rounded-md text-sm ${
                                currentPage === page
                                  ? 'bg-primary-600 text-white border-primary-600'
                                  : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="px-2 text-gray-500">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                <DocumentArrowDownIcon className="h-12 w-12" />
              </div>
              <div className="text-lg font-medium text-gray-900 mb-2">No Payment History</div>
              <div className="text-gray-500">Your payment history will appear here once you start billing</div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Actions */}
      {subscription?.subscription && (
        <BillingActions
          subscription={subscription.subscription}
          onUpdate={fetchBillingData}
          onMessage={setMessage}
        />
      )}
    </div>
  );
};

export default Billing;